import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink, Upload, History, ArrowRight } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import Card from '../components/ui/Card'
import ImportHistory from '../components/tms/ImportHistory'
import ImportJobModal from '../components/tms/ImportJobModal'
import JobSelector from '../components/tms/JobSelector'

const TMSIntegrationPage = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [preselectedJobId, setPreselectedJobId] = useState(null)
  const [selectedJob, setSelectedJob] = useState(null)
  const [showImportModal, setShowImportModal] = useState(false)

  // Check for preselected job from URL parameters
  useEffect(() => {
    const jobId = searchParams.get('job')
    if (jobId) {
      setPreselectedJobId(jobId)
      setShowImportModal(true) // Auto-open modal in 'configure' mode for preselected job
    }
  }, [searchParams])

  const handleImportSuccess = (data) => {
    setShowImportModal(false)
    // Could add success notification here
  }

  const handleCloseModal = () => {
    setShowImportModal(false)
    // Optionally remove job parameter from URL
  }

  const handleJobSelect = (job) => {
    setSelectedJob(job)
    setPreselectedJobId(job.id)
  }

  const handleImportClick = () => {
    if (selectedJob || preselectedJobId) {
      setShowImportModal(true)
    }
  }

  return (
    <div className="page-transition space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Translation Import Workflows
        </h1>
        <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
          Import completed translations to your Translation Management System
        </p>
        {preselectedJobId && (
          <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-blue-900 dark:text-blue-100">
              <strong>Job preselected:</strong> {preselectedJobId}
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Ready to import to TMS. Select your project below.
            </p>
          </div>
        )}
      </div>

      {/* Settings Link */}
      <div className="mb-8">
        <Card>
          <Card.Content className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">
                  Configure TMS Projects
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Set up your Phrase API tokens and project configurations
                </p>
              </div>
              <Link
                to="/settings"
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Go to Settings
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </Card.Content>
        </Card>
      </div>

      {/* Main Content */}
      <div className="space-y-8">
        {/* Import Jobs Section */}
        <Card data-testid="import-translation-jobs-section">
          <Card.Header>
            <div className="flex items-center space-x-3">
              <Upload className="w-6 h-6 text-green-600" />
              <div>
                <Card.Title>Import Translation Jobs</Card.Title>
                <Card.Description>
                  Import your completed translation jobs to TMS projects
                </Card.Description>
              </div>
            </div>
          </Card.Header>
          <Card.Content>
            <div className="space-y-6">
              {preselectedJobId && !selectedJob ? (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <p className="text-green-900 dark:text-green-100 font-medium">
                    Job ready for import: {preselectedJobId}
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Ready to import to TMS. Select your project below.
                  </p>
                </div>
              ) : null}

              {/* Job Selector */}
              <JobSelector
                onJobSelect={handleJobSelect}
                selectedJobId={selectedJob?.id || preselectedJobId}
              />

              {/* Import Button */}
              {(selectedJob || preselectedJobId) && (
                <div className="flex justify-center pt-4">
                  <button
                    data-testid="configure-import-tms-button"
                    onClick={handleImportClick}
                    className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    Configure and Import to TMS
                  </button>
                </div>
              )}
            </div>
          </Card.Content>
        </Card>

        {/* Import History Section */}
        <Card>
          <Card.Header>
            <div className="flex items-center space-x-3">
              <History className="w-6 h-6 text-blue-600" />
              <div>
                <Card.Title>{t('tms.import.history.title')}</Card.Title>
                <Card.Description>
                  {t('tms.import.history.description')}
                </Card.Description>
              </div>
            </div>
          </Card.Header>
          <Card.Content>
            <ImportHistory />
          </Card.Content>
        </Card>


        {/* Documentation Reference */}
        <Card>
          <Card.Header>
            <div className="flex items-center space-x-3">
              <ExternalLink className="w-6 h-6 text-purple-600" />
              <div>
                <Card.Title>Need Help?</Card.Title>
                <Card.Description>
                  Comprehensive guides and troubleshooting
                </Card.Description>
              </div>
            </div>
          </Card.Header>
          <Card.Content>
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Complete TMS Integration Guide
                </h4>
                <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                  Find detailed workflow instructions, troubleshooting tips, and best practices in our documentation.
                </p>
                <Link
                  to="/docs"
                  className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                >
                  View TMS Integration Documentation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <h5 className="font-medium text-gray-900 dark:text-white mb-1 text-sm">
                    Quick Start
                  </h5>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    5-step workflow guide for first-time setup
                  </p>
                </div>
                <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <h5 className="font-medium text-gray-900 dark:text-white mb-1 text-sm">
                    Troubleshooting
                  </h5>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Common issues and solutions
                  </p>
                </div>
              </div>
            </div>
          </Card.Content>
        </Card>
      </div>

      {/* Import Job Modal */}
      <ImportJobModal
        isOpen={showImportModal}
        onClose={handleCloseModal}
        onSuccess={handleImportSuccess}
        preSelectedJobId={preselectedJobId}
        preSelectedJobTitle={`Job ${preselectedJobId}`}
      />
    </div>
  )
}

export default TMSIntegrationPage